import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { Select } from "@/components/ui/select";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, notFound, useNavigate } from "@tanstack/react-router";

/**
 * `/projects/:slug/releases/:releaseId/edit` — edit or delete a
 * release. Changes are pushed to GitLab first.
 */

const INPUT_CLS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

type ReleaseDetailResponse = {
  release: {
    id: string;
    name: string;
    description: string | null;
    dueDate: string | null;
    status: "open" | "locked" | "closed";
    gitlabMilestoneId: number | null;
  };
};

export const Route = createFileRoute("/_app/projects/$slug/releases/$releaseId/edit")({
  loader: async ({ params }) => {
    try {
      return await api.get<ReleaseDetailResponse>(
        `/api/projects/${params.slug}/releases/${params.releaseId}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: EditReleasePage,
});

function EditReleasePage() {
  const { release } = Route.useLoaderData();
  const { slug, releaseId } = Route.useParams();
  const navigate = useNavigate();

  const saveRelease = useApiMutation(
    (body: {
      name: string;
      description: string | null;
      dueDate: string | null;
      status: string;
    }) => api.patch(`/api/projects/${slug}/releases/${releaseId}`, body),
    {
      onSuccess: () =>
        navigate({ to: "/projects/$slug/releases/$releaseId", params: { slug, releaseId } }),
      errorMessage: (err) =>
        err instanceof ApiError
          ? "Couldn't save — you may not have permission."
          : "Couldn't save the release.",
    },
  );
  const deleteRelease = useApiMutation(
    () => api.delete(`/api/projects/${slug}/releases/${releaseId}`),
    {
      onSuccess: () => navigate({ to: "/projects/$slug/releases", params: { slug } }),
      errorMessage: (err) =>
        err instanceof ApiError
          ? "Couldn't delete — only a project manager can delete a release."
          : "Couldn't delete the release.",
    },
  );
  const error = saveRelease.error ?? deleteRelease.error;

  async function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = ((fd.get("name") as string) ?? "").trim();
    if (!name) return;
    await saveRelease.mutate({
      name,
      description: ((fd.get("description") as string) ?? "").trim() || null,
      dueDate: ((fd.get("dueDate") as string) ?? "").trim() || null,
      status: fd.get("status") as string,
    });
  }

  async function remove() {
    if (!confirm("Delete this release? It will be removed from GitLab too.")) return;
    await deleteRelease.mutate();
  }

  const dueIso = release.dueDate ? release.dueDate.slice(0, 10) : "";

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold">Edit Release</h2>
        <Button variant="outline" size="sm" asChild>
          <Link to="/projects/$slug/releases/$releaseId" params={{ slug, releaseId }}>
            Cancel
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{release.name}</CardTitle>
          <CardDescription>
            Changes are pushed to GitLab first.
            {release.gitlabMilestoneId === null && (
              <span className="text-amber-600 block mt-1">
                ⚠ This release isn't linked to GitLab yet — saving will only update Hub. Run a
                gitlab_sync to (re)link.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium leading-none">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                defaultValue={release.name}
                className={INPUT_CLS}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium leading-none">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                defaultValue={release.description ?? ""}
                className={`${INPUT_CLS} resize-y`}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="dueDate" className="text-sm font-medium leading-none">
                  Due date
                </label>
                <DatePicker id="dueDate" name="dueDate" defaultValue={dueIso} />
              </div>

              <div className="space-y-2">
                <label htmlFor="status" className="text-sm font-medium leading-none">
                  Status
                </label>
                <Select
                  name="status"
                  aria-label="Status"
                  defaultValue={release.status}
                  options={[
                    { value: "open", label: "Open (active in GitLab)" },
                    { value: "locked", label: "Locked (Hub-only — frozen scope)" },
                    { value: "closed", label: "Closed" },
                  ]}
                />
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saveRelease.pending}>
                {saveRelease.pending ? "Saving…" : "Save changes"}
              </Button>
              <Button variant="outline" type="button" asChild>
                <Link to="/projects/$slug/releases/$releaseId" params={{ slug, releaseId }}>
                  Cancel
                </Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive text-base">Danger zone</CardTitle>
          <CardDescription>
            Deletes the release in GitLab too. Issues currently assigned to this release stay, just
            lose the release link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="destructive"
            disabled={deleteRelease.pending}
            onClick={remove}
          >
            {deleteRelease.pending ? "Deleting…" : "Delete release"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
